import React from 'react';
import classnames from 'classnames';

import { srJobTypeTextMapping, JOB_STATUS_COMPLETED, JOB_STATUS_FAILED } from 'common/jobConstants';

import {
  TOUR_SHOWING_STATUS_COMPLETED,
  TOUR_SHOWING_FAILURE_REASON_MAPPING,
} from 'common/tourConstants';

import {
  SR_HITLIST_STATUS_COMPLETED,
  SR_HITLIST_TASK_CALL_OUTCOME_ANSWERED,
  SR_HITLIST_TASK_CALL_OUTCOME_NOT_ANSWERED,
  SR_HITLIST_TASK_CALL_OUTCOME_NO_CALL_NEEDED,
} from 'common/hitlistTaskConstants';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrIcon from 'client/components/SrIcon';
import SrLink from 'client/components/SrLink/SrLink';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';

import './SrToursHealth.less';

const getHitlistTaskText = ({ hitlistTask }) => {
  if (!hitlistTask || hitlistTask.status !== SR_HITLIST_STATUS_COMPLETED) {
    return 'Not Called';
  }

  switch (hitlistTask.callOutcome) {
    case SR_HITLIST_TASK_CALL_OUTCOME_ANSWERED:
      return 'Answered';
    case SR_HITLIST_TASK_CALL_OUTCOME_NOT_ANSWERED:
      return 'Not Answered';
    case SR_HITLIST_TASK_CALL_OUTCOME_NO_CALL_NEEDED:
      return 'No Call Needed';
  }
};

const SrHeaderTitle = ({ count, title, color = 'red' }) => {
  return (
    <SrText font="greycliff" fontSize="16px" weight="700" letterSpacing="0.5px" color="grey">
      <SrText
        font="greycliff"
        fontSize="28px"
        weight="800"
        letterSpacing="0.5px"
        paddingBottom="8px"
        display="block"
        color={count === 0 ? 'grey' : color}
      >
        {count}
      </SrText>
      {title}
    </SrText>
  );
};

export const SrToursHealthFailedToursTable = ({ tourHistory, showHitlistTask }) => {
  const getTourDescription = ({ doorHandleLocked, failureReason, comment }) => {
    const description = ` ${
      TOUR_SHOWING_FAILURE_REASON_MAPPING[failureReason] || failureReason || ''
    }
    ${comment ? `"${comment}"` : ''}`;

    const detail = (
      <SrText weight="500" size="12" color={doorHandleLocked ? 'red' : 'grey'}>
        {doorHandleLocked ? ' Door handle reported as locked' : description}
      </SrText>
    );

    return detail;
  };

  return (
    <div styleName="table">
      {tourHistory.map((log, index) => {
        const {
          completedDate,
          createdDate,
          jobTypeId,
          failureReason,
          doorHandleLocked,
          status,
          comment,
          listingId,
          workerNote,
          hitlistTask,
        } = log;

        if (jobTypeId) {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div styleName="row" key={index}>
              <SrFlex styleName="date">
                <SrIcon className="jistix-icon" marginRight="8px" />
                <SrText color="grey" weight="500" size="12">
                  {momentWithAustinTimezone(completedDate).format('MMM D, ha')}
                </SrText>
              </SrFlex>
              <SrFlex styleName="description">
                <SrIcon className="blue-checkmark" />
                <SrText weight="500" size="12" color="blue300">
                  {`${srJobTypeTextMapping[jobTypeId]} order ${status.toLowerCase()}`}
                  {[JOB_STATUS_COMPLETED, JOB_STATUS_FAILED].includes(status) && (
                    <>
                      <br />
                      Notes from the Installer:
                      <br />
                      {workerNote || '--'}
                    </>
                  )}
                </SrText>
              </SrFlex>
              <SrLink to={`/sunsetup/listing-orders/${listingId}`} styleName="actions">
                <SrText color="blue300" size="12">
                  Link to order
                </SrText>
              </SrLink>
            </div>
          );
        }

        return (
          // eslint-disable-next-line react/no-array-index-key
          <div styleName="row" key={index}>
            <SrFlex weight="500" size="12" styleName="date">
              <SrIcon className="sr-icon" marginRight="8px" />
              <SrText color="grey" weight="500" size="12">
                {momentWithAustinTimezone(createdDate).format('MMM D, h:mma')}
              </SrText>
            </SrFlex>
            <div styleName="description tourListCenter">
              <SrText color="grey" weight="500" size="12">
                Failed Tour:
              </SrText>
              {getTourDescription({ doorHandleLocked, failureReason, comment })}
            </div>
            {showHitlistTask && (
              <SrFlex styleName="hitlistTask" width="100px">
                <SrText color="grey" weight="500" size="12">
                  {getHitlistTaskText({ hitlistTask })}
                </SrText>
              </SrFlex>
            )}
            <SrText color="grey" weight="500" size="12" styleName="actions">
              {status === TOUR_SHOWING_STATUS_COMPLETED ? 'Marked as Success' : 'Stayed Failed'}
            </SrText>
          </div>
        );
      })}
    </div>
  );
};

const SrToursHealthFailedTours = ({ onClose, failedToursData }) => {
  return (
    <SrDarkModal onClose={onClose} styleName="SrToursHealthFailedTours">
      <SrModalContent width={800} innerStyle={{ padding: 0 }}>
        <div styleName={classnames('tourHistory modal')}>
          <div styleName="header">
            <SrText
              font="greycliff"
              fontSize="21px"
              weight="800"
              letterSpacing="0"
              paddingBottom="16px"
              display="block"
            >
              Failed tours
            </SrText>
            <SrGridRow columns={4}>
              <SrHeaderTitle
                count={failedToursData.failedToursAfterFixHistory}
                title="Failed after Fix"
              />
              <SrHeaderTitle
                count={failedToursData.totalFailedTours}
                title="Failed total"
                color="orange300"
              />
              <SrHeaderTitle count={failedToursData.totalfixOrders} title="Fix orders" />
              <SrHeaderTitle
                count={failedToursData.totalFailedGeolocationTours}
                title="Geolocation"
              />
            </SrGridRow>
            <SrBox styleName="columns">
              <SrText styleName="columnName">Date</SrText>
              <SrText styleName="columnName" paddingLeft="108px">
                Description
              </SrText>
              <SrText styleName="columnName" paddingLeft="242px">
                Call Outcome
              </SrText>
            </SrBox>
          </div>
          <SrToursHealthFailedToursTable
            tourHistory={failedToursData.tourHistory}
            showHitlistTask
          />
          <div styleName="footer">
            <SrText
              color="blue300"
              cursor="pointer"
              weight="800"
              lineHeight="19px"
              onClick={onClose}
            >
              Hide all failed tours
            </SrText>
          </div>
        </div>
      </SrModalContent>
    </SrDarkModal>
  );
};

SrToursHealthFailedTours.displayName = 'SrToursHealthFailedTours';

export default SrToursHealthFailedTours;
